import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { StoreProvider } from './context/StoreContext';
import { AppLayout } from './components/layout/AppLayout';
import { TenantGate } from './components/layout/TenantGate';

import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import RegisterPage from './pages/auth/RegisterPage';
import AcceptInvitePage from './pages/auth/AcceptInvitePage';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import AlertsPage from './pages/AlertsPage';
import ProductsPage from './pages/products/ProductsPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import CostManagementPage from './pages/costs/CostManagementPage';
import ReceiptCapturePage from './pages/costs/ReceiptCapturePage';
import ProcessingStatusPage from './pages/costs/ProcessingStatusPage';
import ReceiptReviewPage from './pages/costs/ReceiptReviewPage';
import PendingReviewsPage from './pages/costs/PendingReviewsPage';
import SuppliersPage from './pages/suppliers/SuppliersPage';
import ImportsPage from './pages/imports/ImportsPage';
import InventoryPage from './pages/inventory/InventoryPage';
import ReportsPage from './pages/reports/ReportsPage';
import {
  SalesReportPage, ProfitabilityReportPage, CostChangesReportPage,
  InventoryVarianceReportPage, ProfitLeaksReportPage, SuppliersReportPage
} from './pages/reports/ReportPages';
import SettingsPage from './pages/settings/SettingsPage';
import UsersPage from './pages/settings/UsersPage';
import BillingPage from './pages/settings/BillingPage';
import StoreDataPage from './pages/data/StoreDataPage';
import AuditReportPage from './pages/audit/AuditReportPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />

      {/* Protected — inside AppLayout */}
      <Route path="/*" element={
        <RequireAuth>
          <StoreProvider>
            <TenantGate>
              <AppLayout>
                <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:productId" element={<ProductDetailPage />} />
                <Route path="/costs" element={<CostManagementPage />} />
                <Route path="/costs/capture" element={<ReceiptCapturePage />} />
                <Route path="/costs/processing/:documentId" element={<ProcessingStatusPage />} />
                <Route path="/costs/review/:documentId" element={<ReceiptReviewPage />} />
                <Route path="/costs/pending" element={<PendingReviewsPage />} />
                <Route path="/suppliers" element={<SuppliersPage />} />
                <Route path="/imports" element={<ImportsPage />} />
                <Route path="/data" element={<StoreDataPage />} />
                <Route path="/audit" element={<AuditReportPage />} />
                <Route path="/connections" element={<Navigate to="/data" replace />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/reports/sales" element={<SalesReportPage />} />
                <Route path="/reports/profitability" element={<ProfitabilityReportPage />} />
                <Route path="/reports/cost-changes" element={<CostChangesReportPage />} />
                <Route path="/reports/inventory-variance" element={<InventoryVarianceReportPage />} />
                <Route path="/reports/profit-leaks" element={<ProfitLeaksReportPage />} />
                <Route path="/reports/suppliers" element={<SuppliersReportPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/users" element={<UsersPage />} />
                <Route path="/settings/billing" element={<BillingPage />} />
                </Routes>
              </AppLayout>
            </TenantGate>
          </StoreProvider>
        </RequireAuth>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}


